import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import PeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import SecurityIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import CampaignIcon from '@mui/icons-material/CampaignOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoffOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import QuizIcon from '@mui/icons-material/QuizOutlined';
import HistoryEduIcon from '@mui/icons-material/HistoryEduOutlined';
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import TrackChangesIcon from '@mui/icons-material/TrackChangesOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import RuleFolderIcon from '@mui/icons-material/RuleFolderOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import KeyIcon from '@mui/icons-material/KeyOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

export const NAV_GROUPS = ['Overview', 'Management', 'CRM', 'Operations', 'Finance', 'Administration'] as const;
export type NavGroup = (typeof NAV_GROUPS)[number];

/** Role-based notification/pending-action indicator system — keys into
 * NotificationSummary (see types/notification.ts). Only nav items with a
 * real, unambiguous, already-existing "go act on this queue" page get one.
 * `documents` (Phase 51) lands on Students, where the documentsPending
 * click-through filter already lives — never a second Documents page. */
export type NotificationBadgeKey =
  | 'leadApprovals'
  | 'personalAppointments'
  | 'applications'
  | 'payments'
  | 'documents'
  | 'feeChangeApprovals'
  | 'unreadNotifications';

export interface NavItem {
  label: string;
  path: string;
  icon: typeof DashboardIcon;
  group: NavGroup;
  permission?: { module: string; action: string };
  badgeKey?: NotificationBadgeKey;
  /** Only these roles see it, whatever their permissions (Phase 100:
   * Student Logins is Super Admin only). The backend enforces the same. */
  roles?: string[];
  /** A sidebar submenu (expand/collapse) under this item. Each child is a
   * page under the parent's path; without its own `permission` a child
   * needs the parent's. */
  children?: NavChild[];
}

export interface NavChild {
  label: string;
  path: string;
  permission?: { module: string; action: string };
}

/** Every Master Data page, in menu order — the one list both the sidebar
 * submenu and the Master Data pages use. Each route in App.tsx sits under
 * /master-data and is guarded like the parent: master_data:view and the
 * Super Admin role. */
export const MASTER_DATA_CHILDREN: NavChild[] = [
  { label: 'Countries', path: '/master-data/countries' },
  { label: 'Geography', path: '/master-data/geography' },
  { label: 'Universities & Courses', path: '/master-data/academic-catalog' },
  { label: 'Payment & Fee Structures', path: '/master-data/fee-structures' },
  { label: 'Academic Levels', path: '/master-data/academic-levels' },
  { label: 'Intakes', path: '/master-data/intakes' },
  { label: 'Currencies', path: '/master-data/currencies' },
  { label: 'Lead Sources', path: '/master-data/lead-sources' },
  { label: 'Lead Statuses', path: '/master-data/lead-statuses' },
  { label: 'Payment Categories', path: '/master-data/payment-categories' },
  { label: 'SSCL & Exchange Rate', path: '/master-data/sscl-exchange-rate' },
  { label: 'System Email', path: '/master-data/system-email' },
  { label: 'Document Types', path: '/master-data/document-types' },
  { label: 'Visa Statuses', path: '/master-data/visa-statuses' },
  { label: 'Task Types', path: '/master-data/task-types' },
  { label: 'Email Templates', path: '/master-data/email-templates' },
  { label: 'WhatsApp Templates', path: '/master-data/whatsapp-templates' },
];

/** The children of `item` this user may see (each child's own permission,
 * else the parent's). */
export const visibleNavChildren = (
  item: Pick<NavItem, 'children' | 'permission'>,
  hasPermission: (module: string, action: string) => boolean,
): NavChild[] =>
  (item.children ?? []).filter((child) => {
    const permission = child.permission ?? item.permission;
    return !permission || hasPermission(permission.module, permission.action);
  });

/** Phase 76: Branch Manager must never see the Administration section,
 * independent of whatever module:action permissions that role happens to
 * hold (several of them — users/branches/master_data — are deliberately
 * kept for legitimate non-Administration dropdowns elsewhere; see
 * permissions.ts). This is therefore a separate, role-based check layered
 * on top of the ordinary permission check everywhere a NAV_ITEM is used
 * for visibility or route authorization (Sidebar, routeAuthorization.ts,
 * App.tsx's per-route guards). */
const ADMINISTRATION_BLOCKED_ROLES = ['Branch Manager'];

/** Phase 96: Finance Admin works only in the Finance group (plus its own
 * Notifications); Finance Officer too since Phase 97. Their permissions
 * already exclude every other module except Payments (payments:view) —
 * whose general page is replaced for them by the Finance pages — so this
 * keeps their sidebar finance-only. The backend enforces the actual data
 * rules. */
export const FINANCE_WORKSPACE_ROLES = ['Finance Admin', 'Finance Officer'];
const FINANCE_WORKSPACE_EXTRA_PATHS = ['/notifications'];

export const isNavItemVisible = (
  item: Pick<NavItem, 'group'> & { path?: string; roles?: string[] },
  roleName: string | undefined,
): boolean => {
  if (item.roles && (!roleName || !item.roles.includes(roleName))) return false;
  if (roleName && FINANCE_WORKSPACE_ROLES.includes(roleName)) {
    return item.group === 'Finance' || (!!item.path && FINANCE_WORKSPACE_EXTRA_PATHS.includes(item.path));
  }
  return !(item.group === 'Administration' && !!roleName && ADMINISTRATION_BLOCKED_ROLES.includes(roleName));
};

export const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/',
    icon: DashboardIcon,
    group: 'Overview',
    permission: { module: 'dashboard', action: 'view' },
  },
  {
    label: 'Management Reports',
    path: '/management-reports',
    icon: InsightsOutlinedIcon,
    group: 'Management',
    permission: { module: 'management_reports', action: 'view' },
  },
  // Phase 96 — the Finance workspace (Finance Admin, Director, Super
  // Admin hold finance:view). Listed straight after Dashboard so it's
  // Finance Admin's landing page (getDefaultRoute takes the first allowed
  // item in this array); the sidebar still shows the group in NAV_GROUPS
  // order, after Operations.
  {
    label: 'Finance Dashboard',
    path: '/finance',
    icon: AccountBalanceOutlinedIcon,
    group: 'Finance',
    permission: { module: 'finance', action: 'view' },
  },
  {
    label: 'Student Payments',
    path: '/finance/students',
    icon: SchoolIcon,
    group: 'Finance',
    permission: { module: 'finance', action: 'view' },
  },
  {
    label: 'Payment Confirmations',
    path: '/finance/confirmations',
    icon: PendingActionsOutlinedIcon,
    group: 'Finance',
    permission: { module: 'finance', action: 'view' },
    badgeKey: 'payments',
  },
  {
    label: 'Invoices',
    path: '/finance/invoices',
    icon: DescriptionOutlinedIcon,
    group: 'Finance',
    permission: { module: 'finance', action: 'view' },
  },
  {
    label: 'Receipts',
    path: '/finance/receipts',
    icon: ReceiptOutlinedIcon,
    group: 'Finance',
    permission: { module: 'finance', action: 'view' },
  },
  {
    label: 'Payment Ledger',
    path: '/finance/ledger',
    icon: ReceiptLongOutlinedIcon,
    group: 'Finance',
    permission: { module: 'finance', action: 'view' },
  },
  {
    label: 'Finance Reports',
    path: '/finance/reports',
    icon: SummarizeOutlinedIcon,
    group: 'Finance',
    permission: { module: 'finance', action: 'view' },
  },
  {
    label: 'Notifications',
    path: '/notifications',
    icon: NotificationsOutlinedIcon,
    group: 'Overview',
    permission: { module: 'notifications', action: 'view' },
    badgeKey: 'unreadNotifications',
  },
  {
    label: 'Inquiries',
    path: '/inquiries',
    icon: MarkEmailUnreadIcon,
    group: 'CRM',
    permission: { module: 'inquiries', action: 'view' },
  },
  {
    label: 'Leads',
    path: '/leads',
    icon: ContactsIcon,
    group: 'CRM',
    permission: { module: 'leads', action: 'view' },
  },
  {
    label: 'Lead Distribution',
    path: '/lead-distribution',
    icon: RuleFolderIcon,
    group: 'CRM',
    // Deliberately leads:assign, not leads:view — only Super Admin/
    // Director/Branch Manager (the Central Lead Officer role in practice)
    // can assign leads at all, so Counselors never see this nav item.
    permission: { module: 'leads', action: 'assign' },
  },
  {
    label: 'Lead Approvals',
    path: '/lead-approvals',
    icon: FactCheckIcon,
    group: 'CRM',
    // leads:approve — Super Admin/Director/Lead Manager only, never a
    // Counselor (who can't approve their own or anyone else's lead).
    permission: { module: 'leads', action: 'approve' },
    badgeKey: 'leadApprovals',
  },
  {
    label: 'Fee Change Approvals',
    path: '/fee-change-approvals',
    icon: RequestQuoteOutlinedIcon,
    group: 'CRM',
    // fee_changes:approve — Super Admin/Director/Branch Manager only,
    // never the Counselor/Admission Officer who can only request a
    // change, never approve their own (§40).
    permission: { module: 'fee_changes', action: 'approve' },
    badgeKey: 'feeChangeApprovals',
  },
  {
    label: 'Personal Appointments',
    path: '/personal-appointments',
    icon: EventAvailableOutlinedIcon,
    group: 'CRM',
    permission: { module: 'personal_appointments', action: 'view' },
    badgeKey: 'personalAppointments',
  },
  {
    label: 'Students',
    path: '/students',
    icon: SchoolIcon,
    group: 'CRM',
    permission: { module: 'students', action: 'view' },
    badgeKey: 'documents',
  },
  // Alumni Management (Phase 105) — graduates and former students, kept
  // apart from the active student payment/admissions pages.
  {
    label: 'Alumni',
    path: '/alumni',
    icon: WorkspacePremiumOutlinedIcon,
    group: 'CRM',
    permission: { module: 'alumni', action: 'view' },
  },
  {
    // Application form (2026-11-06): portal submissions waiting for review.
    label: 'Application Reviews',
    path: '/application-reviews',
    icon: AssignmentTurnedInOutlinedIcon,
    group: 'CRM',
    permission: { module: 'students', action: 'update' },
  },
  {
    label: 'Student Change Requests',
    path: '/student-change-requests',
    icon: EditNoteOutlinedIcon,
    group: 'CRM',
    permission: { module: 'students', action: 'update' },
  },
  // Phase 94: everyone who can see students sees the requests in their own
  // scope; only a Super Admin gets Approve/Reject on the page.
  {
    label: 'Intake Transfers',
    path: '/intake-transfers',
    icon: SwapHorizOutlinedIcon,
    group: 'CRM',
    permission: { module: 'students', action: 'view' },
  },
  {
    label: 'Admissions',
    path: '/admissions',
    icon: AssignmentTurnedInIcon,
    group: 'CRM',
    permission: { module: 'academic', action: 'view' },
    badgeKey: 'applications',
  },
  {
    label: 'Payments',
    path: '/payments',
    icon: PaymentsIcon,
    group: 'CRM',
    permission: { module: 'payments', action: 'view' },
    badgeKey: 'payments',
  },
  {
    label: 'Campaigns',
    path: '/campaigns',
    icon: CampaignIcon,
    group: 'CRM',
    permission: { module: 'email', action: 'view' },
  },
  // Phase 93: both are student lists, so they use students:view (with the
  // backend's usual branch/Counselor scope), like Students itself.
  {
    label: 'Entrance Exam',
    path: '/entrance-exam',
    icon: QuizIcon,
    group: 'CRM',
    permission: { module: 'students', action: 'view' },
  },
  {
    label: 'FIS Exam',
    path: '/fis-exam',
    icon: HistoryEduIcon,
    group: 'CRM',
    permission: { module: 'students', action: 'view' },
  },
  // Directly before Ready to Fly; same permission, since it's the same
  // ready_to_fly data (see FullyPaidStudentsTable).
  {
    label: 'Batches to Departures',
    path: '/batches-to-departures',
    icon: GroupsIcon,
    group: 'Operations',
    permission: { module: 'ready_to_fly', action: 'view' },
  },
  // Phase 95: the step between Batches to Departures and Ready to Fly.
  {
    label: 'Final Documentation Checklist',
    path: '/final-documentation',
    icon: FactCheckIcon,
    group: 'Operations',
    permission: { module: 'ready_to_fly', action: 'view' },
  },
  {
    label: 'Ready to Fly',
    path: '/ready-to-fly',
    icon: FlightTakeoffIcon,
    group: 'Operations',
    permission: { module: 'ready_to_fly', action: 'view' },
  },
  {
    label: 'Reports',
    path: '/reports',
    icon: AssessmentIcon,
    group: 'Operations',
    permission: { module: 'reports', action: 'view' },
  },
  {
    label: 'Targets',
    path: '/targets',
    icon: TrackChangesIcon,
    group: 'Operations',
    permission: { module: 'targets', action: 'view' },
  },
  {
    label: 'Audit Logs',
    path: '/audit-logs',
    icon: HistoryIcon,
    group: 'Administration',
    permission: { module: 'audit_logs', action: 'view' },
  },
  {
    label: 'Users',
    path: '/users',
    icon: PeopleIcon,
    group: 'Administration',
    permission: { module: 'users', action: 'view' },
  },
  {
    label: 'Student Logins',
    path: '/student-logins',
    icon: KeyIcon,
    group: 'Administration',
    permission: { module: 'students', action: 'view' },
    roles: ['Super Admin'],
  },
  {
    label: 'Roles',
    path: '/roles',
    icon: SecurityIcon,
    group: 'Administration',
    permission: { module: 'roles', action: 'view' },
  },
  {
    label: 'Branches',
    path: '/branches',
    icon: BusinessIcon,
    group: 'Administration',
    permission: { module: 'branches', action: 'view' },
  },
  {
    label: 'Database Backups',
    path: '/database-backups',
    icon: BackupOutlinedIcon,
    group: 'Administration',
    permission: { module: 'audit_logs', action: 'view' },
    roles: ['Super Admin'],
  },
  {
    label: 'Master Data',
    path: '/master-data',
    icon: StorageIcon,
    group: 'Administration',
    permission: { module: 'master_data', action: 'view' },
    // Master Data screens are Super Admin only (2026-11-02). Other roles
    // keep master_data:view so their forms' dropdowns (lead statuses,
    // sources, countries, universities…) can still load the lists.
    roles: ['Super Admin'],
    children: MASTER_DATA_CHILDREN,
  },
];
